import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { calculateDassScores } from '../../utils/assessmentScoring';
import DassResults from './DassResults';

interface DassAssessmentProps {
  onBack: () => void;
}

const questions = [
  { id: 1, text: 'I found myself getting upset by quite trivial things' },
  { id: 2, text: 'I was aware of dryness of my mouth' },
  { id: 3, text: 'I couldn\'t seem to experience any positive feeling at all' },
  { id: 4, text: 'I experienced breathing difficulty (eg, excessively rapid breathing, breathlessness in the absence of physical exertion)' },
  { id: 5, text: 'I just couldn\'t seem to get going' },
  { id: 6, text: 'I tended to over-react to situations' },
  { id: 7, text: 'I had a feeling of shakiness (eg, legs going to give way)' },
  { id: 8, text: 'I found it difficult to relax' },
  { id: 9, text: 'I found myself in situations that made me so anxious I was most relieved when they ended' },
  { id: 10, text: 'I felt that I had nothing to look forward to' },
  { id: 11, text: 'I found myself getting upset rather easily' },
  { id: 12, text: 'I felt that I was using a lot of nervous energy' },
  { id: 13, text: 'I felt sad and depressed' },
  { id: 14, text: 'I found myself getting impatient when I was delayed in any way (eg, elevators, traffic lights, being kept waiting)' },
  { id: 15, text: 'I had a feeling of faintness' },
  { id: 16, text: 'I felt that I had lost interest in just about everything' },
  { id: 17, text: 'I felt I wasn\'t worth much as a person' },
  { id: 18, text: 'I felt that I was rather touchy' },
  { id: 19, text: 'I perspired noticeably (eg, hands sweaty) in the absence of high temperatures or physical exertion' },
  { id: 20, text: 'I felt scared without any good reason' },
  { id: 21, text: 'I felt that life wasn\'t worthwhile' },
  { id: 22, text: 'I found it hard to wind down' },
  { id: 23, text: 'I had difficulty in swallowing' },
  { id: 24, text: 'I couldn\'t seem to get any enjoyment out of the things I did' },
  { id: 25, text: 'I was aware of the action of my heart in the absence of physical exertion (eg, sense of heart rate increase, heart missing a beat)' },
  { id: 26, text: 'I felt down-hearted and blue' },
  { id: 27, text: 'I found that I was very irritable' },
  { id: 28, text: 'I felt I was close to panic' },
  { id: 29, text: 'I found it hard to calm down after something upset me' },
  { id: 30, text: 'I feared that I would be "thrown" by some trivial but unfamiliar task' },
  { id: 31, text: 'I was unable to become enthusiastic about anything' },
  { id: 32, text: 'I found it difficult to tolerate interruptions to what I was doing' },
  { id: 33, text: 'I was in a state of nervous tension' },
  { id: 34, text: 'I felt I was pretty worthless' },
  { id: 35, text: 'I was intolerant of anything that kept me from getting on with what I was doing' },
  { id: 36, text: 'I felt terrified' },
  { id: 37, text: 'I could see nothing in the future to be hopeful about' },
  { id: 38, text: 'I felt that life was meaningless' },
  { id: 39, text: 'I found myself getting agitated' },
  { id: 40, text: 'I was worried about situations in which I might panic and make a fool of myself' },
  { id: 41, text: 'I experienced trembling (eg, in the hands)' },
  { id: 42, text: 'I found it difficult to work up the initiative to do things' }
];

const DassAssessment: React.FC<DassAssessmentProps> = ({ onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showInstructions, setShowInstructions] = useState(true);

  if (showInstructions) {
    return (
      <div className="flex flex-col h-full">
        <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
          <div className="flex items-center mb-6">
            <button
              onClick={onBack}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Emotional Assessment</h2>
          </div>
          
          <div className="space-y-4 mb-8">
            <p className="text-gray-600">
              Please read each statement and select a number 0, 1, 2 or 3 which indicates how much the statement applied to you over the past week.
              There are no right or wrong answers. Do not spend too much time on any statement.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Rating Scale:</h3>
              <ul className="space-y-2">
                <li>0 = Did not apply to me at all</li>
                <li>1 = Applied to me to some degree, or some of the time</li>
                <li>2 = Applied to me to a considerable degree, or a good part of time</li>
                <li>3 = Applied to me very much, or most of the time</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border-t">
          <button
            onClick={() => setShowInstructions(false)}
            className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  if (currentQuestion >= questions.length) {
    const scores = calculateDassScores(answers);
    return <DassResults scores={scores} onBack={onBack} />;
  }

  const handleAnswer = (score: number) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion].id]: score
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Question {currentQuestion + 1}</h2>
              <span className="text-sm text-gray-500">
                Progress: {Math.round((currentQuestion / questions.length) * 100)}%
              </span>
            </div>
            
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${(currentQuestion / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="mb-6">
            <p className="text-lg text-gray-800 mb-6">{questions[currentQuestion].text}</p>
            
            <div className="grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((score) => (
                <button
                  key={score}
                  onClick={() => handleAnswer(score)}
                  className={`
                    p-4 rounded-lg border-2 transition-all
                    ${answers[questions[currentQuestion].id] === score
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="block font-semibold mb-1">{score}</span>
                  <span className="text-sm text-gray-600">
                    {score === 0 ? 'Did not apply to me at all' :
                     score === 1 ? 'Applied to some degree' :
                     score === 2 ? 'Applied considerably' :
                     'Applied very much'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-white border-t mt-auto">
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className={`
              px-4 py-2 rounded-lg
              ${currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            Previous
          </button>
          
          <div className="text-sm text-gray-500">
            {currentQuestion + 1} / {questions.length}
          </div>

          {currentQuestion === questions.length - 1 && (
            <button
              onClick={() => {
                const scores = calculateDassScores(answers);
                setCurrentQuestion(questions.length);
              }}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              View Results
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DassAssessment;